/**
 * App — the container that binds the reducer-only store to the pure `GameView`.
 * It holds no game logic: it subscribes to authoritative client state and maps
 * it to `GameView` props (ui/31 §1, D22/R9).
 */
import { useState, useSyncExternalStore } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { Action, GameState, PlayerId } from '@cards/engine';
import type { DeckDefinition } from '@cards/cards';
import type { ClientStore } from './client/index.js';
import type { BrowserProfile } from './client/profile.js';
import { usePresence } from './client/presence.js';
import type { PresenceProfileSource } from './client/presence.js';
import { MatchDeckPicker } from './decks/MatchDeckPicker.js';
import type { DeckApi } from './decks/api.js';
import type { SavedDeckSelectionRef } from '../server/protocol.js';
import type { DeckReturnContext, DeckReturnStorage } from './decks/returnContext.js';
import {
  Button,
  DeckSelection,
  DelayedLoader,
  GameView,
  MatchResultActionsContext,
  Screen,
  StatusScreen,
  type GameViewProps,
} from './ui/index.js';
import { Lobby } from './ui/Lobby.js';
import { handleInAppLinkClick } from './client/navigation.js';
import { TurnPlaybackContext } from './ui/TurnPlayback.js';
import { useTurnPlayback } from './client/turnPlayback.js';
import type { FastOnboardingProgress, OnboardingApi } from './onboarding/api.js';
import { GameOnboardingTour } from './onboarding/tours.js';

export interface AppProps {
  store: ClientStore;
  /**
   * Pure affordance gate (engine.validate on the redacted snapshot) — used only
   * to disable/highlight; authority stays server-side (D22).
   */
  canApply?: (state: GameState, viewerId: PlayerId, action: Action) => boolean;
  /** Fixture/e2e override; production renders the canonical @cards/cards catalog. */
  deckDefinitions?: readonly DeckDefinition[];
  /** Frozen account-owned picker authority for this concrete match entry. */
  savedDeckPicker?: {
    returnContext: DeckReturnContext;
    preparedSelection?: SavedDeckSelectionRef;
    onPreparedApplied?: () => void;
    /** Returns the deck choice to the home page when the intent cannot be applied. */
    onChoiceRequired?: () => void;
    api?: DeckApi;
    storage?: DeckReturnStorage;
    navigate?: (path: string) => void;
  } | undefined;
  /** Fail-closed account-bound seat loaded without an active account session. */
  savedDeckAccountRequired?: boolean;
  /** Both exact deck snapshots were locked atomically by the challenge bootstrap. */
  fixedDeckMatch?: boolean;
  challengeMatch?: {
    onRetry: () => Promise<void>;
    onSaveDeck: () => Promise<Readonly<{ deckId: string }>>;
  };
  quickMatch?: {
    profile: BrowserProfile;
    profileSource?: PresenceProfileSource;
    startedAt: number;
    onCancel: () => void;
  };
  /** Best-effort presence for private-link games; absence never blocks play. */
  presenceProfile?: BrowserProfile;
  presenceProfileSource?: PresenceProfileSource;
  onboardingGameTour?: {
    csrfToken: string;
    api: OnboardingApi;
    onCompleted: (progress: FastOnboardingProgress) => void;
  };
}

export function SessionCredentialErrorView({ message }: { message: string }): ReactElement {
  return (
    <StatusScreen
      variant="error"
      data-testid="session-storage-error"
      role="alert"
      heading="Не удалось сохранить сессию"
      message={message}
    />
  );
}

export function App({
  store,
  canApply,
  deckDefinitions,
  savedDeckPicker,
  savedDeckAccountRequired = false,
  fixedDeckMatch = false,
  challengeMatch,
  quickMatch,
  presenceProfile,
  presenceProfileSource = 'local',
  onboardingGameTour,
}: AppProps): ReactElement {
  const cs = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  const quickWaiting = quickMatch !== undefined
    && (cs.roomState === undefined || cs.roomState.phase === 'waiting_for_players');
  const presence = usePresence(
    quickMatch?.profile ?? presenceProfile,
    quickWaiting && cs.status !== 'error',
    quickMatch?.profileSource ?? presenceProfileSource,
  );

  if (cs.status === 'error') {
    if (cs.serverUpdated === true || cs.invalidSession === true) {
      const serverUpdated = cs.serverUpdated === true;
      return (
        <StatusScreen
          variant="error"
          data-testid={serverUpdated ? 'server-updated' : 'invalid-session'}
          heading={serverUpdated ? 'Сервер обновлён' : 'Сессия недоступна'}
          message={cs.lastError ?? (serverUpdated
            ? 'Старая комната несовместима с текущей версией игры.'
            : 'Ссылка или сессия устарела, истекла либо сервер был перезапущен.')}
          action={(
            <Button
              as="a"
              href="/"
              onClick={handleInAppLinkClick}
              className="cards-home-button"
              data-testid="create-new-game-after-update"
            >
              На главную
            </Button>
          )}
        />
      );
    }
    return (
      <StatusScreen
        variant="error"
        data-testid="connection-error"
        role="alert"
        message={<>Ошибка: {cs.lastError ?? 'неизвестная ошибка'}</>}
        action={(
          <Button
            as="a"
            href="/"
            onClick={handleInAppLinkClick}
            className="cards-home-button"
            data-testid="new-game-after-error"
          >
            На главную
          </Button>
        )}
      />
    );
  }
  if (quickMatch !== undefined && quickWaiting) {
    // The search state renders inline on the lobby (timer in place of the primary
    // action, "Отменить поиск" in place of the bot action) — no separate page.
    return (
      <Lobby
        profile={quickMatch.profile}
        profileSource={quickMatch.profileSource ?? presenceProfileSource}
        search={{
          startedAt: quickMatch.startedAt,
          presence,
          connecting: cs.viewerId === undefined,
          reconnecting: cs.status === 'connecting' && cs.roomState !== undefined,
          ...(cs.lastError !== undefined ? { connectionError: cs.lastError } : {}),
          onCancel: quickMatch.onCancel,
        }}
      />
    );
  }
  if (cs.status === 'connecting' && (cs.snapshot !== undefined || cs.roomState !== undefined)) {
    return (
      <StatusScreen
        variant="reconnecting"
        panelSize="compact"
        role="status"
        aria-live="polite"
        heading="Соединение прервано"
      >
        <div className="cards-kit-status-reconnecting-copy">Идёт переподключение…</div>
        {cs.lastError !== undefined ? (
          <small className="cards-kit-status-detail">{cs.lastError}</small>
        ) : null}
      </StatusScreen>
    );
  }
  if (cs.viewerId === undefined) {
    // The same full-screen loader as the auth/deck-lock phases around it: the
    // match-entry chain must read as ONE continuous loading state, not a
    // loader → text flash → loader sequence.
    return <DelayedLoader testId="connecting-loader" />;
  }

  if (cs.roomState !== undefined && (cs.roomState.phase !== 'playing' || cs.snapshot === undefined)) {
    if (fixedDeckMatch) return <DelayedLoader testId="connecting-loader" />;
    if (savedDeckAccountRequired) {
      return (
        <Screen
          as="main"
          className="cards-status"
          data-testid="saved-deck-account-required"
          role="status"
        >
          Эта игровая сессия привязана к аккаунту. Войдите и повторите подключение,
          чтобы продолжить выбор колоды.
        </Screen>
      );
    }
    if (savedDeckPicker !== undefined) {
      return (
        <MatchDeckPicker
          room={cs.roomState}
          viewerId={cs.viewerId}
          returnContext={savedDeckPicker.returnContext}
          {...(savedDeckPicker.preparedSelection === undefined
            ? {}
            : { preparedSelection: savedDeckPicker.preparedSelection })}
          {...(savedDeckPicker.onPreparedApplied === undefined
            ? {}
            : { onPreparedApplied: savedDeckPicker.onPreparedApplied })}
          {...(savedDeckPicker.onChoiceRequired === undefined
            ? {}
            : { onChoiceRequired: savedDeckPicker.onChoiceRequired })}
          {...(savedDeckPicker.api === undefined ? {} : { api: savedDeckPicker.api })}
          {...(savedDeckPicker.storage === undefined ? {} : { storage: savedDeckPicker.storage })}
          {...(savedDeckPicker.navigate === undefined ? {} : { navigate: savedDeckPicker.navigate })}
          {...(cs.lastError !== undefined ? { error: cs.lastError } : {})}
          {...(cs.lastRejectedClientSeq === undefined
            ? {}
            : { rejectionClientSeq: cs.lastRejectedClientSeq })}
          {...(cs.lastAckedClientSeq === undefined
            ? {}
            : { acknowledgedClientSeq: cs.lastAckedClientSeq })}
          onSelect={store.selectSavedDeck}
          onLock={store.lockDeck}
        />
      );
    }
    return (
      <DeckSelection
        room={cs.roomState}
        viewerId={cs.viewerId}
        {...(deckDefinitions !== undefined ? { decks: deckDefinitions } : {})}
        {...(cs.lastError !== undefined ? { error: cs.lastError } : {})}
        onSelect={store.selectDeck}
        onLock={store.lockDeck}
      />
    );
  }

  if (cs.snapshot === undefined) {
    // Same continuous loader: this phase sits between the deck auto-lock
    // loader and the first game snapshot.
    return <DelayedLoader testId="connecting-loader" />;
  }

  const viewerNickname = quickMatch?.profile.nickname ?? presenceProfile?.nickname;
  const challengeResultActions = fixedDeckMatch && cs.snapshot.status.phase === 'ended'
    ? (
        <ChallengeMatchResultActions
          victory={cs.snapshot.status.winner === cs.viewerId}
          onRetry={challengeMatch?.onRetry}
          onSaveDeck={challengeMatch?.onSaveDeck}
        />
      )
    : undefined;
  const activeGame = (
    <ActiveGame
      clientState={{ ...cs, snapshot: cs.snapshot, viewerId: cs.viewerId }}
      store={store}
      canApply={canApply}
      {...(onboardingGameTour === undefined ? {} : { onboardingGameTour })}
      {...(viewerNickname !== undefined ? { viewerNickname } : {})}
      {...(challengeResultActions === undefined ? {} : { matchResultActions: challengeResultActions })}
    />
  );
  return activeGame;
}

function ChallengeMatchResultActions({
  victory,
  onRetry,
  onSaveDeck,
}: Readonly<{
  victory: boolean;
  onRetry: (() => Promise<void>) | undefined;
  onSaveDeck: (() => Promise<Readonly<{ deckId: string }>>) | undefined;
}>): ReactElement {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [savedDeckId, setSavedDeckId] = useState<string>();

  const retry = async (): Promise<void> => {
    if (onRetry === undefined || retrying) return;
    setRetrying(true);
    setRetryError(undefined);
    try {
      await onRetry();
    } catch {
      setRetryError('Не удалось запустить бой ещё раз.');
      setRetrying(false);
    }
  };

  const saveDeck = async (): Promise<void> => {
    if (onSaveDeck === undefined || saving) return;
    setSaving(true);
    setSaveError(undefined);
    try {
      const saved = await onSaveDeck();
      setSavedDeckId(saved.deckId);
    } catch {
      setSaveError('Не удалось сохранить колоду этого боя.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {retryError === undefined ? null : (
        <div className="cards-match-result-action-error" role="alert">
          {retryError}
        </div>
      )}
      {saveError === undefined ? null : (
        <div className="cards-match-result-action-error" role="alert">
          {saveError}
        </div>
      )}
      <div className="cards-match-result-actions">
        <Button
          variant="secondary"
          disabled={retrying || saving || onRetry === undefined}
          onClick={() => void retry()}
        >
          {retrying ? 'Запускаем…' : 'Ещё раз'}
        </Button>
        {savedDeckId === undefined ? (
          <Button
            variant="secondary"
            disabled={saving || retrying || onSaveDeck === undefined}
            onClick={() => void saveDeck()}
          >
            {saving ? 'Сохраняем…' : 'Сохранить эту колоду'}
          </Button>
        ) : (
          <Button
            as="a"
            variant="secondary"
            href={`/decks/${savedDeckId}`}
            onClick={handleInAppLinkClick}
          >
            Открыть в редакторе
          </Button>
        )}
        <Button as="a" href="/challenges" onClick={handleInAppLinkClick}>
          {victory ? 'Получить награду' : 'К челленджам'}
        </Button>
      </div>
    </>
  );
}

interface ActiveGameProps {
  clientState: ReturnType<ClientStore['getState']> & { snapshot: GameState; viewerId: PlayerId };
  store: ClientStore;
  canApply: AppProps['canApply'];
  viewerNickname?: string;
  onboardingGameTour?: NonNullable<AppProps['onboardingGameTour']>;
  matchResultActions?: ReactNode;
}

function ActiveGame({
  clientState: cs,
  store,
  canApply,
  viewerNickname,
  onboardingGameTour,
  matchResultActions,
}: ActiveGameProps): ReactElement {
  const deckDefinitionIds: Partial<Record<PlayerId, string>> = {};
  const playerNames: Partial<Record<PlayerId, string>> = {};
  for (const player of cs.roomState?.players ?? []) {
    if (player.deckDefinitionId !== undefined) deckDefinitionIds[player.playerId] = player.deckDefinitionId;
    if (player.nickname !== undefined) playerNames[player.playerId] = player.nickname;
  }
  if (viewerNickname !== undefined && viewerNickname.length > 0 && playerNames[cs.viewerId] === undefined) {
    playerNames[cs.viewerId] = viewerNickname;
  }
  const playback = useTurnPlayback(cs.snapshot, cs.seq, cs.log, {
    viewerId: cs.viewerId,
    playerNames,
  });
  // Narrow subscriptions: cue-only playback steps must not re-render the game
  // container — cards subscribe to their own slices through the context store.
  const snapshot = useSyncExternalStore(playback.subscribe, playback.getState, playback.getState);
  const visibleLog = useSyncExternalStore(playback.subscribe, playback.getVisibleLog, playback.getVisibleLog);
  const isPlaying = useSyncExternalStore(
    playback.subscribe,
    () => playback.getVisuals().isPlaying,
    () => playback.getVisuals().isPlaying,
  );

  const viewerId = cs.viewerId;
  const props: GameViewProps = {
    state: snapshot,
    viewerId,
    log: visibleLog,
    onAction: store.send,
    deckDefinitionIds,
    playerNames,
    ...(!isPlaying && cs.pendingChoice !== undefined ? { pendingChoice: cs.pendingChoice } : {}),
    ...(canApply !== undefined
      ? { canApply: (action: Action) => canApply(snapshot, viewerId, action) }
      : {}),
  };
  return (
    <MatchResultActionsContext.Provider value={matchResultActions}>
      <TurnPlaybackContext.Provider value={playback}>
        <GameView {...props} />
        {onboardingGameTour === undefined ? null : (
          <GameOnboardingTour
            enabled
            round={cs.snapshot.round}
            phase={cs.snapshot.phase}
            playbackIdle={!isPlaying}
            csrfToken={onboardingGameTour.csrfToken}
            api={onboardingGameTour.api}
            onCompleted={onboardingGameTour.onCompleted}
          />
        )}
      </TurnPlaybackContext.Provider>
    </MatchResultActionsContext.Provider>
  );
}
